import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Video, Package, CalendarDays, Sparkles, HeartHandshake } from "lucide-react";
import { trackBookingStart } from "@/components/GoogleAnalytics";

const BOOKING_URL = "/book";

interface HowItWorksSectionProps {
  onBookingClick: () => void;
}

const HowItWorksSection = ({ onBookingClick }: HowItWorksSectionProps) => {
  const steps = [
    {
      icon: Video,
      number: "1",
      title: "Free 20-Min Video Call",
      description: "We see your space, you ask questions — no obligation, no pressure."
    },
    {
      icon: Package,
      number: "2",
      title: "Choose Your Package",
      description: "Flat rate pricing, no hidden fees. Pick the Reset or Fresh Start package."
    },
    {
      icon: CalendarDays,
      number: "3",
      title: "We Schedule",
      description: "Usually within 1–2 weeks. We work around your calendar."
    },
    {
      icon: Sparkles,
      number: "4",
      title: "We Organize",
      description: "We bring supplies, work together, and transform your space."
    },
    {
      icon: HeartHandshake,
      number: "5",
      title: "You Maintain",
      description: "With our simple systems, follow-up check-ins, and ongoing support."
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-light mb-6">
            How Booking Works
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Simple, transparent process from consultation to lasting transformation.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 max-w-6xl mx-auto mb-12">
          {steps.map((step, index) => (
            <Card key={index} className="text-center border-border hover:shadow-lg transition-all duration-300 bg-background relative">
              <CardContent className="p-6">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary text-primary-foreground rounded-full w-7 h-7 flex items-center justify-center text-xs font-bold">
                  {step.number}
                </div>
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full mb-4 mt-3">
                  <step.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-base font-semibold mb-2 text-balance">
                  {step.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {step.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button
            onClick={() => {
              trackBookingStart();
              window.location.href = BOOKING_URL;
            }}
            size="lg"
            className="luxury-gradient text-primary-foreground font-medium px-8 py-4"
          >
            Book Your Free Call
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={() => window.open('sms:+16167270343?body=Hi! Here are photos of my space:', '_self')}
            className="px-8 py-4"
          >
            Text Photos: 616.727.0343
          </Button>
        </div>
        <p className="text-sm text-muted-foreground mt-4 text-center">
          Free consultation • Flat-rate packages • No hidden fees
        </p>
      </div>
    </section>
  );
};

export default HowItWorksSection;
